import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Dumbbell,
  Zap,
  ChevronRight,
  Check,
  X,
  Sparkles,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  Target,
  AlertTriangle,
  Info
} from 'lucide-react';

const PRIORITY_COLORS = {
  high: 'border-red-500/30 bg-red-500/5',
  medium: 'border-yellow-500/30 bg-yellow-500/5',
  low: 'border-blue-500/30 bg-blue-500/5'
};

const TYPE_ICONS = {
  weight_increase: ArrowUp,
  rep_increase: TrendingUp,
  deload: ArrowDown
};

const TYPE_COLORS = {
  weight_increase: 'text-green-400',
  rep_increase: 'text-blue-400',
  deload: 'text-yellow-400'
};

export default function ProgressionSuggestions({ workoutId, onApplied }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [expanded, setExpanded] = useState(true);

  const fetchSuggestions = useCallback(async () => {
    try {
      const url = workoutId 
        ? `${API}/progression/suggestions?workout_id=${workoutId}&use_ai=true`
        : `${API}/progression/suggestions?use_ai=true`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    } finally {
      setLoading(false);
    }
  }, [token, workoutId]);

  useEffect(() => {
    if (token) {
      fetchSuggestions();
    }
  }, [token, fetchSuggestions]);

  const applySuggestion = async (suggestionId) => {
    if (!workoutId) {
      toast.error('No workout selected');
      return;
    }
    
    setApplying(suggestionId);
    try {
      const response = await axios.post(
        `${API}/progression/apply/${suggestionId}?workout_id=${workoutId}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setSuggestions(prev => prev.filter(s => s.id !== suggestionId));
      
      if (onApplied) {
        onApplied(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply progression');
    } finally {
      setApplying(null);
    }
  };

  const dismissSuggestion = async (suggestionId) => {
    try {
      await axios.delete(`${API}/progression/suggestion/${suggestionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuggestions(prev => prev.filter(s => s.id !== suggestionId));
      toast.success('Suggestion dismissed');
    } catch (error) {
      toast.error('Failed to dismiss suggestion');
    }
  };

  const autoApplyAll = async () => {
    setApplying('all');
    try {
      const response = await axios.post(`${API}/progression/auto-apply`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      fetchSuggestions();
      
      if (onApplied) {
        onApplied(response.data);
      }
    } catch (error) {
      toast.error('Failed to auto-apply progressions');
    } finally {
      setApplying(null);
    }
  };

  if (loading) {
    return (
      <div className="card-tactical p-4">
        <div className="flex items-center justify-center py-4">
          <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (suggestions.length === 0) {
    return null;
  }

  return (
    <div data-testid="progression-suggestions" className="card-tactical">
      {/* Header */}
      <div 
        className="flex items-center justify-between p-4 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-yellow-500" />
          <h3 className="font-heading text-lg text-white uppercase">
            AI PROGRESSIONS
          </h3>
          <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
            {suggestions.length}
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          {expanded && suggestions.length > 1 && (
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => { e.stopPropagation(); autoApplyAll(); }}
              disabled={applying === 'all'}
              className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10"
            >
              {applying === 'all' ? (
                <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <Zap className="w-3 h-3 mr-1" />
              )}
              Apply All
            </Button>
          )}
          <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </div>
      </div>

      {/* Suggestions List */}
      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          <p className="text-xs text-gray-500 mb-3">
            Based on your workout history, here are AI-powered progression recommendations:
          </p>
          
          {suggestions.map((suggestion) => {
            const TypeIcon = TYPE_ICONS[suggestion.type] || TrendingUp;
            const typeColor = TYPE_COLORS[suggestion.type] || 'text-gray-400';
            
            return (
              <div
                key={suggestion.id}
                data-testid={`suggestion-${suggestion.id}`}
                className={`p-4 rounded-sm border ${PRIORITY_COLORS[suggestion.priority] || PRIORITY_COLORS.low}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Exercise Name & Type */}
                    <div className="flex items-center gap-2 mb-2">
                      <Dumbbell className="w-4 h-4 text-gray-400" />
                      <span className="font-medium text-white">{suggestion.exercise_name}</span>
                      <span className={`flex items-center gap-1 text-xs ${typeColor}`}>
                        <TypeIcon className="w-3 h-3" />
                        {suggestion.type.replace('_', ' ')}
                      </span>
                      {suggestion.priority === 'high' && (
                        <span className="px-1.5 py-0.5 text-xs bg-red-500/20 text-red-400 rounded">
                          High Priority
                        </span>
                      )}
                    </div>
                    
                    {/* Change */}
                    <div className="flex items-center gap-3 mb-2">
                      <div className="text-sm text-gray-400">
                        <span className="text-gray-500">Current:</span>{' '}
                        <span className="text-white font-mono">
                          {suggestion.current_value} {suggestion.type.includes('weight') ? 'lbs' : 'reps'}
                        </span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-600" />
                      <div className="text-sm">
                        <span className="text-gray-500">Suggested:</span>{' '}
                        <span className={`font-mono font-medium ${typeColor}`}>
                          {suggestion.suggested_value} {suggestion.type.includes('weight') ? 'lbs' : 'reps'}
                        </span>
                        <span className={`ml-2 text-xs ${typeColor}`}>
                          ({suggestion.change})
                        </span>
                      </div>
                    </div>
                    
                    {/* Reason */}
                    <p className="text-xs text-gray-500 mb-2">
                      {suggestion.reason}
                    </p>
                    
                    {/* AI Tip */}
                    {suggestion.ai_tip && (
                      <div className="flex items-start gap-2 p-2 bg-purple-500/10 rounded-sm">
                        <Sparkles className="w-3 h-3 text-purple-400 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-purple-300">{suggestion.ai_tip}</p>
                      </div>
                    )}
                    
                    {/* Confidence */}
                    <div className="flex items-center gap-2 mt-2">
                      <div className="flex-1 h-1.5 bg-secondary rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-yellow-500 to-green-500"
                          style={{ width: `${(suggestion.confidence || 0.5) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500">
                        {Math.round((suggestion.confidence || 0.5) * 100)}% confidence
                      </span>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex flex-col gap-2 ml-4">
                    <Button
                      size="sm"
                      onClick={() => applySuggestion(suggestion.id)}
                      disabled={applying === suggestion.id || !workoutId}
                      className="bg-green-600 hover:bg-green-700"
                      title={workoutId ? 'Apply to workout' : 'Select a workout first'}
                    >
                      {applying === suggestion.id ? (
                        <RefreshCw className="w-3 h-3 animate-spin" />
                      ) : (
                        <Check className="w-3 h-3" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => dismissSuggestion(suggestion.id)}
                      className="text-gray-400 hover:text-red-400"
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
          
          {!workoutId && (
            <div className="flex items-center gap-2 p-2 bg-blue-500/10 rounded-sm text-xs text-blue-400">
              <Info className="w-3 h-3" />
              Select a workout to apply progressions individually
            </div>
          )}
        </div>
      )}
    </div>
  );
}
